import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { profileAPI } from "../services/api";
import "./Dashboard.scss";

// Map API status (e.g. "under-review", "submitted") to display label
function statusToDisplay(status) {
  if (!status) return "Submitted";
  const s = String(status).toLowerCase().replace(/\s+/g, "-");
  const map = {
    "submitted": "Submitted",
    "under-review": "Under Review",
    "interview": "Under Review",
    "shortlisted": "Shortlisted",
    "rejected": "Rejected",
    "accepted": "Hired",
    "hired": "Hired",
  };
  return map[s] || "Submitted";
}

function Dashboard() {
  const navigate = useNavigate();
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profileRes, applicationsRes] = await Promise.all([
          profileAPI.getProfile(),
          profileAPI.getApplications(),
        ]);
        if (profileRes.data?.success && profileRes.data?.data?.profile) {
          const p = profileRes.data.data.profile;
          setProfileCompleteness(p.profileCompleteness ?? 0);
        }
        if (applicationsRes.data?.success && applicationsRes.data?.data?.applications) {
          const list = applicationsRes.data.data.applications.map((app) => ({
            id: app._id,
            jobTitle: app.position?.title ?? "—",
            company: app.position?.company ?? "—",
            status: app.status,
            statusDisplay: statusToDisplay(app.status),
            appliedDate: app.createdAt,
          }));
          setApplications(list);
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
        setError(err.response?.data?.message || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getStatusColor = (statusDisplay) => {
    const colors = {
      "Submitted": "var(--primary)",
      "Under Review": "#F59E0B",
      "Shortlisted": "var(--secondary)",
      "Rejected": "#EF4444",
      "Hired": "var(--secondary)",
    };
    return colors[statusDisplay] || "#6B7280";
  };

  if (loading) {
    return (
      <>
        <Header />
        <section className="dashboard-section">
          <div className="tf-container">
            <header className="dashboard-header">
              <h1 className="dashboard-title">Dashboard</h1>
              <p className="dashboard-subtitle">Track your applications and manage your profile</p>
            </header>
            <div className="dashboard-loading">
              <div className="app-spinner" />
              <p>Loading dashboard...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Gotop />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <section className="dashboard-section">
          <div className="tf-container">
            <header className="dashboard-header">
              <h1 className="dashboard-title">Dashboard</h1>
              <p className="dashboard-subtitle">Track your applications and manage your profile</p>
            </header>
            <div className="dashboard-error">
              <p>{error}</p>
              <button type="button" className="btn-retry" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </section>
        <Footer />
        <Gotop />
      </>
    );
  }

  const applicationsCount = applications.length;
  const shortlistedCount = applications.filter((app) => app.statusDisplay === "Shortlisted").length;
  const rejectedCount = applications.filter((app) => app.statusDisplay === "Rejected").length;
  const underReviewCount = Math.max(0, applicationsCount - shortlistedCount - rejectedCount);

  return (
    <>
      <Header />
      <section className="dashboard-section">
        <div className="dashboard-bg" aria-hidden="true" />
        <div className="tf-container">
          <header className="dashboard-header">
            <p className="dashboard-welcome">Welcome back</p>
            <h1 className="dashboard-title">Dashboard</h1>
            <p className="dashboard-subtitle">Track your applications and manage your profile</p>
          </header>

          <div className="dashboard-grid">
            {/* Profile Completeness Card */}
            <article className="dashboard-card profile-card">
              <div className="card-icon-wrap profile-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h2>Profile Completeness</h2>
              <div className="profile-progress">
                <div className="progress-circle" role="img" aria-label={`Profile ${profileCompleteness}% complete`}>
                  <svg className="progress-ring" width="140" height="140" viewBox="0 0 140 140">
                    <defs>
                      <linearGradient id="dashboard-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="var(--primary)" />
                        <stop offset="100%" stopColor="var(--secondary)" />
                      </linearGradient>
                      <filter id="progress-glow">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <circle className="progress-ring-circle-bg" strokeWidth="10" fill="transparent" r="62" cx="70" cy="70" />
                    <circle
                      className="progress-ring-circle"
                      stroke="url(#dashboard-gradient)"
                      strokeWidth="10"
                      fill="transparent"
                      r="62"
                      cx="70"
                      cy="70"
                      strokeDasharray={`${2 * Math.PI * 62}`}
                      strokeDashoffset={`${2 * Math.PI * 62 * (1 - profileCompleteness / 100)}`}
                      transform="rotate(-90 70 70)"
                      filter="url(#progress-glow)"
                    />
                  </svg>
                  <div className="progress-percentage">{profileCompleteness}%</div>
                </div>
                <p className="profile-completeness-text">
                  Complete your profile to increase your chances of getting hired
                </p>
                <button type="button" className="btn-update-profile" onClick={() => navigate("/profile")}>
                  Update Profile
                </button>
              </div>
            </article>

            {/* Quick Stats */}
            <article className="dashboard-card stats-card">
              <h2>Quick Stats</h2>
              <div className="stats-grid">
                <div className="stat-item stat-applications">
                  <div className="stat-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <div className="stat-number">{applicationsCount}</div>
                  <div className="stat-label">Applications</div>
                </div>
                <div className="stat-item stat-shortlisted">
                  <div className="stat-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </div>
                  <div className="stat-number">{shortlistedCount}</div>
                  <div className="stat-label">Shortlisted</div>
                </div>
                <div className="stat-item stat-review">
                  <div className="stat-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="stat-number">{underReviewCount}</div>
                  <div className="stat-label">Under Review</div>
                </div>
              </div>
            </article>

            {/* Messages Card – to be implemented in the future */}
            {/* <article className="dashboard-card messages-card">
              <div className="card-header">
                <h2>Messages</h2>
                <button type="button" className="btn-view-all" onClick={() => navigate("/inbox")}>
                  View All
                </button>
              </div>
              <div className="messages-preview">
                <div className="messages-empty-icon" aria-hidden="true">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <p className="no-messages">No new messages</p>
                <p className="messages-hint">Employers will contact you here about your applications</p>
              </div>
            </article> */}

            {/* Recent Applications */}
            <article className="dashboard-card applications-card">
              <div className="card-header">
                <h2>Recent Applications</h2>
                <button type="button" className="btn-view-all" onClick={() => navigate("/applications")}>
                  View All
                </button>
              </div>
              <div className="applications-list">
                {applications.length > 0 ? (
                  applications.map((application) => (
                    <div key={application.id} className="application-item">
                      <div className="application-avatar" aria-hidden="true">
                        {(application.company && application.company !== "—") ? application.company.charAt(0).toUpperCase() : "?"}
                      </div>
                      <div className="application-info">
                        <h3>{application.jobTitle}</h3>
                        <p className="company-name">{application.company}</p>
                        <p className="applied-date">
                          Applied on {application.appliedDate ? new Date(application.appliedDate).toLocaleDateString() : "—"}
                        </p>
                      </div>
                      <div className="application-status">
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(application.statusDisplay) }}
                        >
                          {application.statusDisplay}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-applications">
                    <div className="no-applications-icon" aria-hidden="true">
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <p>You haven&apos;t applied to any jobs yet</p>
                    <button type="button" className="btn-browse-jobs" onClick={() => navigate("/careers")}>
                      Browse Jobs
                    </button>
                  </div>
                )}
              </div>
            </article>
          </div>
        </div>
      </section>
      <Footer />
      <Gotop />
    </>
  );
}

export default Dashboard;
